import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import MiniDrawer from './Drawer';
import useStyles from './styles';
import { getUser } from '../../requests/api/user';
import Snackbar from '../Snackbar';
import { getCompanies, getCompaniesPerPage, getUserCompanies } from '../../requests/api/company';
import { SUPER_ADMIN } from '../../utils/const/user';
import companyActions from '../../actions/company';
import ticketActions from '../../actions/tickets';
import AuthService from '../../services/authService';
import useDebounce from '../../utils/useDebounce';

const COMPANY_SEARCH_LIMIT = 30;

function Layout({ children }) {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const [userData, setUserData] = useState('');
  const [companies, setCompanies] = useState([]);
  const [err, setErr] = useState('');
  const account = useSelector((state) => state.account);
  const company = useSelector((state) => state.company);
  const ticketsQuantity = useSelector((state) => state.tickets.pendingTickets);
  const dispatch = useDispatch();
  const [companySearch, setCompanySearch] = useState('');
  const [searchedCompanies, setSearchedCompanies] = useState(null);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const debouncedCompanySearch = useDebounce(companySearch, 400);
  const isSuperAdmin = account.user.role === SUPER_ADMIN;

  const setSessionWithCompany = async (companyId) => {
    const token = localStorage.getItem('accessToken');
    await AuthService.setSession(token, companyId);
  };

  useEffect(() => {
    const getCompaniesRequest = async () => {
      try {
        const response = await getUser();
        const { user } = response.data.data;
        setUserData(user);
        const companiesResponse = user.role === SUPER_ADMIN
          ? await getCompanies() : await getUserCompanies(user.id);
        if (companiesResponse.success) {
          const allCompanies = companiesResponse.data.data.companies;
          const companiesOptions = allCompanies.map((c) => ({ name: c.name, value: c.id }));
          setCompanies(companiesOptions);
          if (company.companyId === 0 && companiesOptions.length > 0) {
            const companyId = companiesOptions[0].value;
            await setSessionWithCompany(companyId);
            dispatch(companyActions.setCompany(companyId));
          } else {
            await setSessionWithCompany(company.companyId);
            dispatch(companyActions.refresh());
          }
        }
      } catch {
        setErr(t('layout.userError'));
      }
    };
    getCompaniesRequest();
  }, [company.companyId, dispatch, t]);

  // Only SUPER_ADMIN can use /companies-per-page; other roles filter their loaded list locally
  useEffect(() => {
    if (!isSuperAdmin || !debouncedCompanySearch) {
      setSearchedCompanies(null);
      setSearchingCompanies(false);
      return undefined;
    }
    let active = true;
    const searchCompaniesRequest = async () => {
      setSearchingCompanies(true);
      const response = await getCompaniesPerPage(
        0,
        COMPANY_SEARCH_LIMIT,
        encodeURIComponent(debouncedCompanySearch),
        'name',
      );
      if (!active) return;
      if (response.success) {
        const { Companies } = response.data.data.companies;
        setSearchedCompanies(Companies.map((c) => ({ name: c.name, value: c.id })));
      } else {
        setErr(response.data.errorMessage);
      }
      setSearchingCompanies(false);
    };
    searchCompaniesRequest();
    return () => {
      active = false;
    };
  }, [debouncedCompanySearch, isSuperAdmin]);

  useEffect(() => {
    const fetchPendingTickets = () => {
      dispatch(ticketActions.actionFetchPendingTickets());
    };
    fetchPendingTickets();
  }, [company, dispatch]);

  useEffect(() => {
    if (userData) {
      window.intercomSettings = {
        ...window.intercomSettings,
        user_id: userData.id,
        app_id: 'pv0x64b5',
        name: userData.fullName,
        email: userData.email,
        user_role: userData.role,
      };
    } else {
      window.intercomSettings = {
        ...window.intercomSettings,
        app_id: 'pv0x64b5',
        user_id: account.user.email,
        name: account.user.email,
        email: account.user.email,
        user_role: account.user.role,
      };
    }
  }, [userData, account]);

  const handleCompanyChange = async (companyId) => {
    localStorage.setItem('companyId', companyId);
    await setSessionWithCompany(companyId);
    dispatch(companyActions.setCompany(companyId));
    history.push('/home');
  };

  return (
    <div className={classes.root}>
      <MiniDrawer
        companies={companies}
        currentCompany={company.companyId}
        handleCompanyChange={handleCompanyChange}
        searchedCompanies={searchedCompanies}
        searchingCompanies={searchingCompanies}
        handleCompanySearch={setCompanySearch}
        ticketsQuantity={ticketsQuantity}
      />
      {children}
      <Snackbar
        open={err !== ''}
        message={err}
        severity="error"
        onClose={() => setErr('')}
      />
    </div>

  );
}

export default Layout;
